import { RowNode } from 'ag-grid-community';
import { AgGridColumn } from 'ag-grid-react/lib/agGridColumn';
import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { HcpGrid, HcpRequestStateUI, UserSearch } from '.';
import { useUpdateUser, useGetUsers, API_DEMO_APPLICATION_NAME, useForgotPassword } from '../api';
import { AnyType, GeneralObject } from '../interfaces';
import { AppRootState, AuthUser, loadConfig, navigateToUserNew } from '../redux';
import {
  ActionColumnActions,
  actionsColumnDefinition,
  clientApplicationNameSelector,
  featureGridColumnsSelector,
  generalColumnDefinition,
  getConfigurationDataKey,
  getFeatureGridColumnProps,
  isEmptyObject,
  phoneValueFormatter,
  rawHtmlProps
} from '../utils';
import EditUserDetails from './EditUserDetails';
import { FeatureGuard } from './multi-tenant/FeatureGuard';
import { ROUTE_PATHS, standardPortalConfig } from '../utils/constants';

const columnSelector = featureGridColumnsSelector(getConfigurationDataKey('users.table'));
const initialValue = { email: '', phone: '' };

export const UsersTable = ({ searchValue }: { searchValue: string | undefined }) => {
  const [landing, setLanding] = useState(true);
  const [open, setOpen] = useState(false);
  const [updateOperation, setUpdateOperation] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialValue);
  const dispatch = useDispatch();
  // const [searchValue, setSearchValue] = useState<string>();
  const organizationId = useSelector((state: AppRootState) => state.auth.session?.user?.OrganizationId);
  const portal_ApplicationName = useSelector((state: AppRootState) => state.app?.config?.applicationName);
  const { mutate: updateUser } = useUpdateUser({});
  const result = useGetUsers({ lazy: true });
  const verbiageText = useSelector(
    (state: AppRootState) =>
      (((state.app?.entities?.features as AnyType) ?? {})['account-administration-verbiage'] as AnyType)?.data || {}
  );

  const handleResetPassword = (data: any, currentUser: AuthUser | undefined, applicationName: string, result: any) => {
    const isCurrentUser = currentUser?.email === data.email;
    if (isCurrentUser) return;
    const confirmationMessage = `Are you sure you want to reset ${data.firstName} ${data.lastName}'s password?`;
    const confirmation = window.confirm(confirmationMessage);
    if (confirmation) {
      result.refetch({
        queryParams: {
          userName: data.userName,
          applicationName: applicationName
        }
      });
    }
    if (result.data?.errors && result.data.errors.length > 0) {
      const errors = result.data.errors.map((e: { description: any }) => e.description).join(' ');
      alert(errors);
    }
  };

  const onSubmit = async (values: GeneralObject) => {
    setLoading(true);
    setError('');
    try {
      const payload = {
        applicationName: standardPortalConfig.isStandardPortalUrl
          ? String(API_DEMO_APPLICATION_NAME)
          : String(portal_ApplicationName),
        emailAddress: String(values.email),
        cellNumber: String(values.cellNumber)?.replace(/[^\d]/g, ''),
        userName: String(values.userName)
      };

      const updateUserResult = await updateUser(payload);

      if (updateUserResult.errors && updateUserResult.errors.length > 0) {
        const errors = updateUserResult.errors?.map((e) => e.description).join(' ');
        setLoading(false);
        setUpdateOperation(true);
        setError(errors);
        return errors;
      }

      if (updateUserResult?.isSuccess) {
        setLoading(false);
        handleClose();
        dispatch(loadConfig());
        setUpdateOperation(true);
        return;
      }
    } catch (e: any) {
      setLoading(false);
      setError(e.message);
      return { error: e.message };
    }
  };

  const loadData = useCallback(() => {
    if (!organizationId) return;
    if (landing || updateOperation) {
      result.refetch({});
      setUpdateOperation(false);
    }
    setLanding(false);
  }, [organizationId]);

  useEffect(loadData, [loadData, updateOperation]);

  const handleClickOpen = (data: any) => {
    setFormData(data);
    setOpen(true);
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
  };

  const filterUsers = (node: RowNode, value: string | undefined) => {
    const name = `${node.data?.firstName} ${node.data?.lastName}`.toLowerCase();
    const username = node.data?.userName?.toLowerCase();
    const email = node.data?.email?.toLowerCase();
    const cellNumber = node.data?.cellNumber?.toLowerCase();
    return (
      name.includes(value ?? '') ||
      username.includes(value ?? '') ||
      email.includes(value ?? '') ||
      (cellNumber && cellNumber.includes(value ?? ''))
    );
  };

  const columns = useSelector(columnSelector);

  if (!columns) return null;

  return (
    <>
      <HcpRequestStateUI
        loadingUIEnabled
        isLoading={result.loading || !result.data}
        errorUIEnabled
        isError={result.error}
        reloadingEnabled
        reload={loadData}>
        <HcpGrid
          isFilterActive={!!searchValue}
          filterComparator={filterUsers}
          filterValue={searchValue}
          rowData={result.data?.users || []}
          pagination>
          <AgGridColumn
            {...generalColumnDefinition()}
            resizable={true}
            field='firstName'
            {...getFeatureGridColumnProps(columns.firstName)}
          />
          <AgGridColumn
            {...generalColumnDefinition()}
            resizable={true}
            field='lastName'
            {...getFeatureGridColumnProps(columns.lastName)}
          />
          <AgGridColumn
            {...generalColumnDefinition()}
            resizable={true}
            field='userType'
            maxWidth='125'
            {...getFeatureGridColumnProps(columns.userType)}
          />
          <AgGridColumn
            {...generalColumnDefinition()}
            resizable={true}
            field='userName'
            {...getFeatureGridColumnProps(columns.username)}
          />
          <AgGridColumn
            {...generalColumnDefinition()}
            resizable={true}
            field='email'
            {...getFeatureGridColumnProps(columns.email)}
          />
          <AgGridColumn
            {...generalColumnDefinition()}
            resizable={true}
            field='cellNumber'
            {...getFeatureGridColumnProps(columns.phone)}
            valueFormatter={phoneValueFormatter}
            valueGetter={phoneValueFormatter}
          />
          <AgGridColumn
            {...generalColumnDefinition()}
            resizable={true}
            cellRenderer='IsAdminRenderer'
            colId='isAdmin'
            field='isAdmin'
            maxWidth='100'
            {...getFeatureGridColumnProps(columns.admin)}
          />
          <AgGridColumn
            {...generalColumnDefinition()}
            resizable={true}
            cellRenderer='UserStatusRenderer'
            colId='isActive'
            field='isActive'
            //filter='false'
            maxWidth='100'
            {...getFeatureGridColumnProps(columns.status)}
          />
          <AgGridColumn
            resizable={false}
            {...actionsColumnDefinition((node: RowNode): ActionColumnActions[] => {
              const currentUser: AuthUser | undefined = useSelector((state: AppRootState) => state.auth.session?.user);
              const applicationName = String(useSelector(clientApplicationNameSelector));
              const result = useForgotPassword({ lazy: true });

              const isCurrentUser = currentUser?.email === node.data.email;

              const actions: ActionColumnActions[] = [
                {
                  label: 'Edit Profile',
                  icon: <i className='fa-light fa-user'></i>,
                  onClick: () => handleClickOpen(node.data) // Always visible
                }
              ];

              if (!isCurrentUser) {
                actions.push({
                  label: 'Reset Password',
                  icon: <i className='fa-light fa-key'></i>,
                  onClick: () => handleResetPassword(node.data, currentUser, applicationName, result)
                });
              }
              return actions;
            })}
          />
        </HcpGrid>
      </HcpRequestStateUI>
      {!isEmptyObject(verbiageText) && (
        <span className='text-primary ag-theme-alpine' {...rawHtmlProps(verbiageText)} />
      )}
      {open && (
        <EditUserDetails
          isLoading={loading}
          error={error}
          handleClose={handleClose}
          data={formData}
          onSubmit={onSubmit}
        />
      )}
    </>
  );
};
